import { memo, useCallback } from 'react';
import styled from '@emotion/styled';
import type { ChartData, Chat, KolSearchResult } from '@trinity/chat';
import { ChatCitationType, ChatQueryStatus, ChatResponseType } from '@trinity/chat';
import { GenericFormWorkflow } from '@trinity/chat-components/components/Chat.Forms/GenericFormWorkflow.js';
import { ContextSelectorContainer } from '@trinity/chat-components/components/Chat.Header/ContextSelector.Container.js';
import { AgentProgressContainer } from '../Chat.AgentProgress/AgentProgressContainer';
import { AgentProgressContext } from '../Chat.AgentProgress/context';
import { BasicMarkdown } from '../Markdown/Markdown';
import { AudioStatusListener } from './AudioStatusListener';
import { AttachmentsContext } from './context';
import { KolProfileMessage } from './KolSearch.Profile';
import { AvailabilityInsightsMessage } from './KolSearch.Profile.Availability';
import { LocationForm } from './Location.Form';
import { ChartContainer } from './Message.Response.ChartContainer';
import { HcpSelectionMessage } from './Message.Response.HcpSelection';
import { SchedulingAssistantResponse } from './Message.Response.SchedulingAssistant';
import { StreamingSystemUserMessage, SystemUserMessage } from './Message.System';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { SystemUserCancelledMessage } from './Message.System.Cancelled';
import { SystemMessageContainer } from './Message.System.Container';
import { SystemUserErrorMessage } from './Message.System.Error';
import { SystemUserInFlightResponse } from './Message.System.InProgress';
import { MessageWrapper } from './Message.System.Wrapper';
import { UserMessage } from './Message.User';
import { QueryFollowupsContainer } from './Query.FollowupsContainer';
import { QueryEventsContainer } from './QueryEventsContainer';
import { QuerySocketContainer } from './QuerySocketContainer';
import { SegmentationDimensionSelection } from './Segmentation.DimensionSelection';
import { SegmentationResult, SegmentationResultUserMessage } from './Segmentation.Result';
import { SegmentationTagSelection } from './Segmentation.TagSelection';
import { SmartRoutingContainer } from './SmartRouting.Container';

type Props = {
  canRegenerate: boolean;
  item: Chat.InstanceQueryItem;
};

export const QueryItem = memo(
  ({ canRegenerate, item }: Props) => {
    const renderSystemResponse = useCallback(() => {
      if (item.query.response?.type === ChatResponseType.HcpSelection) {
        return (
          <HcpSelectionMessage
            queryId={item.query.id}
            queryIdentifier={item.query.identifier}
            value={item.query.state as Chat.QueryState.HcpSelection}
          />
        );
      }

      if (item.query.response?.type === ChatResponseType.HcpSummary) {
        return (
          <QueryFollowupsContainer queryIdentifier={item.query.identifier} queryStatus={item.query.status}>
            <AudioStatusListener queryIdentifier={item.query.identifier} queryId={item.query.id}>
              <SystemMessageContainer citations={item.query.citations}>
                <KolProfileMessage
                  followupQuestions={item.query.suggestedFollowups}
                  profile={item.query.response.data.profile}
                  queryId={item.query.id}
                />
              </SystemMessageContainer>
            </AudioStatusListener>
          </QueryFollowupsContainer>
        );
      }

      if (item.query.response?.type === ChatResponseType.HcpAvailability) {
        return (
          <MessageContainer>
            <MessageWrapper>
              <SystemMessageContainer
                citations={item.query.citations.filter((x) => x.type === ChatCitationType.FbmsInteractions)}
              >
                <BasicSystemUserMessage>
                  <AvailabilityInsightsMessage
                    citations={item.query.citations}
                    profile={item.query.response.data.profile}
                  />
                </BasicSystemUserMessage>
              </SystemMessageContainer>
            </MessageWrapper>
          </MessageContainer>
        );
      }

      if (item.query.response?.type === ChatResponseType.LocationSelection) {
        return (
          <MessageContainer>
            <MessageWrapper>
              <BasicSystemUserMessage>
                <LocationForm
                  queryId={item.query.id}
                  queryIdentifier={item.query.identifier}
                  value={item.query.state as Chat.QueryState.LocationSelection}
                  hcp={item.query.response.hcp}
                  disabled={!canRegenerate}
                />
              </BasicSystemUserMessage>
            </MessageWrapper>
          </MessageContainer>
        );
      }

      if (item.query.response?.type === ChatResponseType.SchedulingAssistant) {
        return (
          <MessageWrapper>
            <BasicSystemUserMessage>
              <SchedulingAssistantResponse
                queryId={item.query.id}
                state={item.query.state as Chat.QueryState.SchedulingAssistant}
              />
            </BasicSystemUserMessage>
          </MessageWrapper>
        );
      }
      if (item.query.response?.type === ChatResponseType.FormWorkflow) {
        return (
          <MessageWrapper>
            <BasicSystemUserMessage>
              <ContextSelectorContainer>
                <GenericFormWorkflow
                  key={`form-workflow-${item.query.id}-${item.query.identifier}`}
                  queryId={item.query.id}
                  queryIdentifier={item.query.identifier}
                  value={item.query.state as Chat.QueryState.Value<ChatResponseType.FormWorkflow>}
                />
              </ContextSelectorContainer>
            </BasicSystemUserMessage>
          </MessageWrapper>
        );
      }
      if (item.query.response?.type === ChatResponseType.SegmentationTagsNeeded) {
        // Only the latest query should poll for segmentation events.
        // `canRegenerate` is only true for the last item in the message list.
        if (!canRegenerate) {
          return (
            <MessageContainer>
              <BasicSystemUserMessage>
                <SegmentationTagSelection
                  queryId={item.query.id}
                  queryIdentifier={item.query.identifier}
                  value={item.query.state as Chat.QueryState.SegmentationTagsNeeded}
                />
              </BasicSystemUserMessage>
            </MessageContainer>
          );
        }

        return (
          <QuerySocketContainer queryIdentifier={item.query.identifier}>
            <QueryEventsContainer queryIdentifier={item.query.identifier}>
              <MessageContainer>
                <BasicSystemUserMessage>
                  <SegmentationTagSelection
                    queryId={item.query.id}
                    queryIdentifier={item.query.identifier}
                    value={item.query.state as Chat.QueryState.SegmentationTagsNeeded}
                  />
                </BasicSystemUserMessage>
              </MessageContainer>
            </QueryEventsContainer>
          </QuerySocketContainer>
        );
      }
      if (item.query.response?.type === ChatResponseType.SegmentationQuestionsNeeded) {
        const state = item.query.state as Chat.QueryState.SegmentationQuestionsNeeded;
        const selectedTagNames = state.availableTags
          .filter((tag) => state.selectedTags.includes(tag.id))
          .map((tag) => tag.name)
          .join(', ');

        return (
          <QuerySocketContainer queryIdentifier={item.query.identifier}>
            <QueryEventsContainer queryIdentifier={item.query.identifier}>
              <MessageContainer>
                <UserMessage value={`I'd like to analyze ${selectedTagNames} tagged data.`} />
              </MessageContainer>
              <MessageContainer>
                <BasicSystemUserMessage>
                  <SegmentationDimensionSelection
                    queryId={item.query.id}
                    queryIdentifier={item.query.identifier}
                    value={item.query.state as Chat.QueryState.SegmentationQuestionsNeeded}
                  />
                </BasicSystemUserMessage>
              </MessageContainer>
            </QueryEventsContainer>
          </QuerySocketContainer>
        );
      }
      if (item.query.response?.type === ChatResponseType.SegmentationCancelled) {
        return (
          <MessageContainer>
            <BasicSystemUserMessage>
              <div>Segmentation has been cancelled.</div>
            </BasicSystemUserMessage>
          </MessageContainer>
        );
      }
      if (item.query.response?.type === ChatResponseType.SegmentationComplete) {
        return (
          <QuerySocketContainer queryIdentifier={item.query.identifier}>
            <QueryEventsContainer queryIdentifier={item.query.identifier}>
              <MessageContainer>
                <UserMessage
                  value={
                    <SegmentationResultUserMessage
                      queryId={item.query.id}
                      queryIdentifier={item.query.identifier}
                      value={item.query.state as Chat.QueryState.SegmentationComplete}
                      citations={item.query.citations}
                    />
                  }
                />
              </MessageContainer>
              <MessageContainer>
                <SystemMessageContainer citations={item.query.citations}>
                  <BasicSystemUserMessage>
                    <SegmentationResult
                      queryId={item.query.id}
                      queryIdentifier={item.query.identifier}
                      value={item.query.state as Chat.QueryState.SegmentationComplete}
                      citations={item.query.citations}
                    />
                  </BasicSystemUserMessage>
                </SystemMessageContainer>
              </MessageContainer>
            </QueryEventsContainer>
          </QuerySocketContainer>
        );
      }
      if (item.query.response?.type === ChatResponseType.SmartRouting) {
        let kol = null;
        if (
          item.query.response &&
          'data' in item.query.response &&
          item.query.response.data &&
          'kol' in item.query.response.data
        ) {
          kol = (item.query.response.data as { kol?: unknown }).kol ?? null;
        }
        return (
          <SmartRoutingContainer
            item={item.query.response}
            state={item.query.state as Chat.QueryState.Value<ChatResponseType.SmartRouting>}
            queryId={item.query.id}
            queryIdentifier={item.query.identifier}
            hcp={kol as KolSearchResult}
          />
        );
      }

      return (
        <MessageContainer>
          <MessageResponse item={item.query} hasRegenerateFooter={canRegenerate} />
        </MessageContainer>
      );
    }, [item.query, canRegenerate]);

    const renderUserMessage = useCallback(() => {
      if (item.query.hideUserMessage) {
        return null;
      }

      return (
        <MessageContainer>
          <UserMessage value={<BasicMarkdown>{item.query.value}</BasicMarkdown>} />
        </MessageContainer>
      );
    }, [item.query.value, item.query.hideUserMessage]);

    return (
      <>
        {renderUserMessage()}
        {renderSystemResponse()}
      </>
    );
  },
  (prev, next) => prev.item.query === next.item.query && prev.canRegenerate === next.canRegenerate
);

type MessageResponseProps = {
  item: Chat.Query;
  hasRegenerateFooter: boolean;
};

// todo: possibly refactor to inverse the layout and have the current inner UI elements
// like variant selector and other actions contain the message response component
const MessageResponse = ({ item, hasRegenerateFooter }: MessageResponseProps) => {
  switch (item.status) {
    case ChatQueryStatus.LoadingExtended:
    case ChatQueryStatus.Loading: {
      return (
        <QuerySocketContainer queryIdentifier={item.identifier}>
          <QueryEventsContainer queryIdentifier={item.identifier}>
            <AgentProgressContainer queryIdentifier={item.identifier}>
              <SystemUserInFlightResponse />
            </AgentProgressContainer>
          </QueryEventsContainer>
        </QuerySocketContainer>
      );
    }

    case ChatQueryStatus.Error:
      return <SystemUserErrorMessage item={item} traceId={item.sentryTraceId} hasRegenerateFooter={false} />;

    case ChatQueryStatus.Streaming:
      return (
        <QuerySocketContainer queryIdentifier={item.identifier}>
          <QueryEventsContainer queryIdentifier={item.identifier}>
            <StreamingSystemUserMessage item={item} />
          </QueryEventsContainer>
        </QuerySocketContainer>
      );

    case ChatQueryStatus.Cancelled: {
      return <SystemUserCancelledMessage item={item} />;
    }

    case ChatQueryStatus.Complete:
    case ChatQueryStatus.FollowupsAvailable: {
      const chart: ChartData =
        item.response.type === ChatResponseType.Chart
          ? (item.response.data as ChartData)
          : item.response.type === ChatResponseType.BIChartQuantAnalyses
            ? (item.response.data as ChartData)
            : null;

      return (
        <AttachmentsContext.Provider value={item.attachments}>
          <QueryFollowupsContainer queryIdentifier={item.identifier} queryStatus={item.status}>
            <AudioStatusListener queryIdentifier={item.identifier} queryId={item.id}>
              <SystemMessageContainer citations={item.citations}>
                <ChartContainer chart={chart}>
                  <AgentProgressContext.Provider value={item.agentProgress}>
                    <SystemUserMessage item={item} hasRegenerateFooter={hasRegenerateFooter} />
                  </AgentProgressContext.Provider>
                </ChartContainer>
              </SystemMessageContainer>
            </AudioStatusListener>
          </QueryFollowupsContainer>
        </AttachmentsContext.Provider>
      );
    }
  }
};

const MessageContainer = styled.div({
  paddingRight: 20,
  paddingLeft: 20,
});
